<template>
  <div
    class="company-card"
    @click="onCardClick"
  >

    <!-- =====================================================
         COVER
    ====================================================== -->

    <div class="cover">

      <img
        v-if="backgroundUrl"
        :src="backgroundUrl"
        alt=""
        class="cover-image"
      />

      <div class="cover-overlay"></div>


      <button
        type="button"
        class="follow-button"
        :class="{ following: isFollowing }"
        @click.stop="toggleFollow"
      >
        <svg
          v-if="!isFollowing"
          viewBox="0 0 18 18"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M9 4V14"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          />

          <path
            d="M4 9H14"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </svg>

        <svg
          v-else
          viewBox="0 0 18 18"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M4 9.2L7.1 12.2L14 5.7"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <span>
          {{ isFollowing ? "Вы подписаны" : "Подписаться" }}
        </span>
      </button>

    </div>


    <!-- =====================================================
         LOGO
    ====================================================== -->

    <div class="logo-row">

      <div
        class="logo-wrapper"
        :class="{ empty: !logoUrl }"
      >

        <img
          v-if="logoUrl"
          :src="logoUrl"
          alt=""
          class="company-logo"
        />

        <span
          v-else
          class="logo-fallback"
        >
          {{ companyInitial }}
        </span>

      </div>

    </div>


    <!-- =====================================================
         CONTENT
    ====================================================== -->

    <div class="company-info">

      <div class="company-heading">

        <h3 class="company-name">
          {{ name || "Неизвестная компания" }}
        </h3>


        <div class="region-badge">
          <svg
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M8 14C8 14 12 10.5 12 7.2C12 4.85 10.2 3.3 8 3.3C5.8 3.3 4 4.85 4 7.2C4 10.5 8 14 8 14Z"
              stroke="currentColor"
              stroke-width="1.25"
            />

            <circle
              cx="8"
              cy="7.1"
              r="1.25"
              stroke="currentColor"
              stroke-width="1.15"
            />
          </svg>

          <span>
            {{ regionName }}
          </span>
        </div>

      </div>


      <p class="company-specifics">
        {{ specifics || "Специфика не указана" }}
      </p>


      <div class="divider"></div>


      <p class="company-about">
        {{
          about
          || "Компания пока не добавила описание."
        }}
      </p>


      <div class="card-footer">

        <span class="open-label">
          Подробнее
        </span>

        <span class="open-arrow">
          <svg
            viewBox="0 0 18 18"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6.5 4.5L11 9L6.5 13.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>

      </div>

    </div>

  </div>
</template>


<script setup>

import {
  computed,
  ref,
  watch
} from "vue";


const props = defineProps({

  name: String,

  specifics: String,

  logoUrl: String,

  backgroundUrl: String,

  companyId: [
    String,
    Number
  ],

  region: {
    type: [
      String,
      Number
    ],
    default: null
  },

  about: String,

  isFollowed: {
    type: Boolean,
    default: false
  }

});


const emit = defineEmits([
  "companyClicked",
  "follow-toggled"
]);


/* ============================================================
   FOLLOW
============================================================ */

const isFollowing =
  ref(
    props.isFollowed
  );


watch(
  () => props.isFollowed,

  value => {
    isFollowing.value =
      value;
  }
);


function toggleFollow() {

  isFollowing.value =
    !isFollowing.value;


  emit(
    "follow-toggled",
    isFollowing.value
  );

}


/* ============================================================
   CARD CLICK
============================================================ */

function onCardClick(event) {

  if (
    event.target.closest(
      ".follow-button"
    )
  ) {
    return;
  }


  if (
    props.companyId
  ) {

    emit(
      "companyClicked",
      props.companyId
    );

  }

}


/* ============================================================
   REGION
============================================================ */

const regionName =
  computed(() => {

    const id =
      Number(
        props.region
      );


    if (
      !Number.isFinite(id)
    ) {
      return "Регион не указан";
    }


    switch (id) {

      case 1:
        return "Москва";

      case 2:
        return "Санкт-Петербург";

      default:
        return "Регион не указан";

    }

  });


/* ============================================================
   FALLBACK
============================================================ */

const companyInitial =
  computed(() => {

    const value =
      String(
        props.name || "H"
      ).trim();


    return (
      value[0]
      ?.toUpperCase()
      || "H"
    );

  });

</script>


<style scoped>

/* ============================================================
   ROOT
============================================================ */

.company-card {
  position: relative;

  width: 300px;
  min-height: 360px;

  overflow: hidden;

  display: flex;
  flex-direction: column;

  box-sizing: border-box;

  background: #ffffff;

  border:
    1px solid
    rgba(25, 40, 60, 0.09);

  border-radius: 22px;

  box-shadow:
    0
    14px
    38px
    rgba(15, 23, 42, 0.055);

  font-family:
    "Eastman-Regular",
    sans-serif;

  cursor: pointer;

  transition:
    transform
    0.2s ease,
    border-color
    0.2s ease,
    box-shadow
    0.2s ease;
}


.company-card:hover {
  transform:
    translateY(-3px);

  border-color:
    rgba(40, 110, 205, 0.12);

  box-shadow:
    0
    22px
    48px
    rgba(15, 23, 42, 0.10);
}


/* ============================================================
   COVER
============================================================ */

.cover {
  position: relative;

  width: 100%;
  height: 118px;

  flex-shrink: 0;

  overflow: hidden;

  background:
    linear-gradient(
      135deg,
      #eaf1fb,
      #e8e6f8
    );
}


.cover-image {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;
}


.cover-overlay {
  position: absolute;

  inset: 0;

  pointer-events: none;

  background:
    linear-gradient(
      180deg,
      rgba(0,0,0,0.015),
      rgba(0,0,0,0.12)
    );
}


/* ============================================================
   FOLLOW
============================================================ */

.follow-button {
  position: absolute;

  top: 12px;
  right: 12px;

  min-height: 34px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 5px;

  padding:
    0
    11px;

  color: #ffffff;

  background:
    rgba(25, 30, 40, 0.52);

  border:
    1px solid
    rgba(255,255,255,0.24);

  border-radius: 10px;

  backdrop-filter:
    blur(12px);

  -webkit-backdrop-filter:
    blur(12px);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  cursor: pointer;

  transition:
    background-color
    0.18s ease,
    transform
    0.18s ease,
    border-color
    0.18s ease;
}


.follow-button svg {
  width: 13px;
  height: 13px;
}


.follow-button:hover {
  transform:
    translateY(-1px);

  background:
    rgba(25, 30, 40, 0.68);
}


.follow-button.following {
  color: #2e76dc;

  background:
    rgba(255,255,255,0.92);

  border-color:
    rgba(255,255,255,0.55);
}


.follow-button.following:hover {
  background:
    #ffffff;
}


/* ============================================================
   LOGO
============================================================ */

.logo-row {
  height: 0;

  position: relative;

  z-index: 5;
}


.logo-wrapper {
  position: absolute;

  left: 18px;
  top: -29px;

  width: 58px;
  height: 58px;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  background: #ffffff;

  border:
    4px solid
    #ffffff;

  border-radius: 17px;

  box-shadow:
    0
    8px
    22px
    rgba(15, 23, 42, 0.14);
}


.logo-wrapper.empty {
  background:
    linear-gradient(
      135deg,
      #ecf2fb,
      #eceafa
    );
}


.company-logo {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  border-radius: 13px;
}


.logo-fallback {
  color: #477ed7;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 18px;
}


/* ============================================================
   CONTENT
============================================================ */

.company-info {
  flex: 1;

  display: flex;
  flex-direction: column;

  padding:
    43px
    18px
    15px;
}


.company-heading {
  min-width: 0;

  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 8px;
}


.company-name {
  min-width: 0;

  margin: 0;

  color: #2d3239;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 14px;

  line-height: 1.35;

  letter-spacing: -0.2px;

  overflow: hidden;

  text-overflow: ellipsis;

  white-space: nowrap;
}


/* ============================================================
   REGION
============================================================ */

.region-badge {
  max-width: 115px;

  display: inline-flex;
  align-items: center;

  gap: 4px;

  flex-shrink: 0;

  padding:
    5px
    7px;

  color: #6680a8;

  background:
    rgba(60, 105, 175, 0.06);

  border:
    1px solid
    rgba(60, 105, 175, 0.07);

  border-radius: 8px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;

  overflow: hidden;
}


.region-badge svg {
  width: 11px;
  height: 11px;

  flex-shrink: 0;
}


.region-badge span {
  overflow: hidden;

  text-overflow: ellipsis;

  white-space: nowrap;
}


/* ============================================================
   SPECIFICS
============================================================ */

.company-specifics {
  margin:
    6px
    0
    0;

  color: #838891;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  line-height: 1.4;

  display: -webkit-box;

  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  overflow: hidden;
}


/* ============================================================
   DIVIDER
============================================================ */

.divider {
  width: 100%;
  height: 1px;

  margin:
    14px
    0
    12px;

  background:
    rgba(
      25,
      40,
      60,
      0.055
    );
}


/* ============================================================
   ABOUT
============================================================ */

.company-about {
  margin: 0;

  color: #7f858e;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;

  line-height: 1.55;

  display: -webkit-box;

  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;

  overflow: hidden;
}


/* ============================================================
   FOOTER
============================================================ */

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-top: auto;

  padding-top: 14px;
}


.open-label {
  color: #8c9199;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;
}


.open-arrow {
  width: 27px;
  height: 27px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #6282b4;

  background:
    rgba(60, 105, 175, 0.055);

  border-radius: 8px;

  transition:
    transform
    0.2s ease,
    background-color
    0.2s ease;
}


.open-arrow svg {
  width: 13px;
  height: 13px;
}


.company-card:hover
.open-arrow {
  transform:
    translateX(2px);

  background:
    rgba(50, 105, 195, 0.09);
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.company-card {
  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.07
    );

  box-shadow:
    0
    16px
    40px
    rgba(
      0,
      0,
      0,
      0.18
    );
}


html[data-theme="dark"]
.company-card:hover {
  border-color:
    rgba(
      255,
      255,
      255,
      0.1
    );

  box-shadow:
    0
    24px
    52px
    rgba(
      0,
      0,
      0,
      0.30
    );
}


html[data-theme="dark"]
.cover {
  background:
    linear-gradient(
      135deg,
      #2b3038,
      #292735
    );
}


html[data-theme="dark"]
.logo-wrapper {
  background: #24262b;

  border-color: #24262b;

  box-shadow:
    0
    8px
    24px
    rgba(
      0,
      0,
      0,
      0.33
    );
}


html[data-theme="dark"]
.logo-wrapper.empty {
  background:
    linear-gradient(
      135deg,
      #2d3440,
      #302d3d
    );
}


html[data-theme="dark"]
.company-name {
  color: #f0f1f3;
}


html[data-theme="dark"]
.company-specifics {
  color: #a0a5ae;
}


html[data-theme="dark"]
.company-about {
  color: #858a93;
}


html[data-theme="dark"]
.region-badge {
  color: #94b3e2;

  background:
    rgba(
      80,
      130,
      210,
      0.10
    );

  border-color:
    rgba(
      95,
      145,
      225,
      0.12
    );
}


html[data-theme="dark"]
.divider {
  background:
    rgba(
      255,
      255,
      255,
      0.055
    );
}


html[data-theme="dark"]
.open-label {
  color: #747981;
}


html[data-theme="dark"]
.open-arrow {
  color: #91b6ee;

  background:
    rgba(
      80,
      130,
      210,
      0.09
    );
}


html[data-theme="dark"]
.follow-button.following {
  color: #8db9ff;

  background:
    rgba(
      30,
      34,
      40,
      0.84
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.13
    );
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 430px) {

  .company-card {
    width: 100%;

    min-height: 345px;

    border-radius: 20px;
  }


  .cover {
    height: 112px;
  }


  .company-info {
    padding:
      42px
      15px
      14px;
  }


  .company-heading {
    flex-direction: column;

    gap: 6px;
  }


  .region-badge {
    max-width: 100%;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .company-card,
  .follow-button,
  .open-arrow {
    transition: none;
  }

}

</style>